////////////////////////////////////////////
// WC PAIRING MANAGER — ONE GLOBAL PRE-PAIRING FOR THE WHOLE APP
////////////////////////////////////////////
//
// WHY A SINGLETON: ConnectProvider REMOUNTS ON ROUTE CHANGES, SO COMPONENT-
// LOCAL PAIRING STATE STARTED CONCURRENT provider.connect() CALLS ON THE ONE
// SHARED EthereumProvider, AND THE OLD ABORT-ON-CLOSE PATH
// (connector.disconnect()) CLOSED ITS RELAY TRANSPORT MID-SUBSCRIBE — THE
// REPEATED "Connection interrupted while trying to subscribe" ERRORS IN THE
// LOGS (THE RELAY/PROJECT THEMSELVES ARE FINE — PROBED OK FROM NODE).
//
// RULES: ONE ATTEMPT AT A TIME, SHARED ACROSS MOUNTS AND MODAL OPENS; AN
// UNUSED PAIRING IS LEFT TO EXPIRE ON ITS OWN (NEVER TRANSPORT-CLOSED); A
// SEQUENTIAL ABORT→SETTLE→RESTART RUNS ONLY FOR AN EXPLICIT RETRY OR A
// STALE URI. A URI ARRIVING AFTER THE TIMEOUT STILL FLIPS TO ready.
import type { Connector } from "wagmi";
import { wcPreparePairing } from "@/lib/wallet/wc-deeplink";
import * as log from "@/lib/log";

export type WcPhase = "idle" | "preparing" | "ready" | "failed";

// PAIRING URIS EXPIRE AFTER ~5 MIN — TRUST OURS A LITTLE LESS THAN THAT
const URI_FRESH_MS = 4 * 60 * 1000;
// A URI NORMALLY MINTS IN 1-2s — PAST THIS THE ROWS READ failed (SELF-HEALS
// IF THE RELAY EVENTUALLY ANSWERS)
const TIMEOUT_MS = 15_000;

type ConnectFn = (args: { connector: Connector }) => Promise<unknown>;

let phase: WcPhase = "idle";
let uri: string | null = null;
let mintedAt = 0;
let attempt: Promise<unknown> | null = null;
let restarting = false;
const subs = new Set<() => void>();

function set(p: WcPhase) {

  if (phase === p)
    return;
  phase = p;
  for (const f of subs) f();
}

export function wcPhaseSnapshot(): WcPhase {

  return phase;
}

export function subscribeWc(cb: () => void): () => void {

  subs.add(cb);
  return () => { subs.delete(cb); };
}

// THE URI, ONLY WHILE FRESH ENOUGH TO HAND TO A WALLET APP
export function wcUri(): string | null {

  return uri && Date.now() - mintedAt < URI_FRESH_MS ? uri : null;
}

// THE IN-FLIGHT CONNECT — RESOLVES WHEN A WALLET APPROVES THE SESSION
export function wcResult(): Promise<unknown> | null {

  return attempt;
}

function start(connector: Connector, connectAsync: ConnectFn) {

  set("preparing");
  uri = null;

  const timer = setTimeout(() => {

    if (phase === "preparing") {

      log.warn("wallet-evm", "wc pairing timeout — relay unreachable or blocked");
      set("failed");
    }
  }, TIMEOUT_MS);

  attempt = wcPreparePairing({
    connector,
    onUri: (u) => {

      clearTimeout(timer);
      uri = u;
      mintedAt = Date.now();
      set("ready");
      log.info("wallet-evm", "wc pairing ready");
    },
    connectAsync,
  })
    .then((r) => {

      // SESSION APPROVED — THE PAIRING IS SPENT
      clearTimeout(timer);
      uri = null;
      attempt = null;
      set("idle");
      return r;
    })
    .catch((e) => {

      clearTimeout(timer);
      uri = null;
      attempt = null;
      if (!restarting) {

        log.warn("wallet-evm", "wc pairing unavailable", { err: (e as Error)?.message });
        set("failed");
      }
      throw e;
    });
  // OBSERVED VIA wcResult() BY WHOEVER CARES — DON'T LET IT SURFACE UNHANDLED
  attempt.catch(() => { /* HANDLED ABOVE */ });
}

// IDEMPOTENT — CALLED EVERY TIME THE CONNECT MODAL OPENS ON MOBILE
export function ensureWc(connector: Connector, connectAsync: ConnectFn) {

  if (phase === "preparing" || restarting)
    return;
  if (wcUri()) {

    set("ready");
    return;
  }
  // AN OLD ATTEMPT WITH A STALE/CONSUMED URI — REPLACE IT SEQUENTIALLY
  if (attempt) {

    void restart(connector, connectAsync);
    return;
  }
  start(connector, connectAsync);
}

export function retryWc(connector: Connector, connectAsync: ConnectFn, opts: { clearStorage?: boolean } = {}) {

  if (restarting)
    return;
  if (attempt)
    void restart(connector, connectAsync, opts);
  else {

    if (opts.clearStorage) clearWcStorage();
    start(connector, connectAsync);
  }
}

// ABORT → WAIT FOR THE OLD ATTEMPT TO SETTLE → START FRESH. SEQUENTIAL ON
// PURPOSE: CONCURRENT connect() CALLS ARE WHAT INTERRUPTED THE TRANSPORT.
async function restart(connector: Connector, connectAsync: ConnectFn, opts: { clearStorage?: boolean } = {}) {

  restarting = true;
  set("preparing");
  try {

    await connector.disconnect().catch(() => { /* NOTHING LIVE */ });
    await attempt?.catch(() => { /* EXPECTED ABORT REJECTION */ });
  } finally {
    restarting = false;
  }
  if (opts.clearStorage) clearWcStorage();
  start(connector, connectAsync);
}

// STALE-STORE REPAIR — MONTHS OF ABORTED ATTEMPTS LEAVE wc@2 RECORDS (OLD
// PAIRINGS, DEAD SUBSCRIPTIONS) THAT THE RELAYER RESTORES AND RE-SUBSCRIBES
// ON INIT; A POISONED CACHE SURFACES AS "Subscribing to <topic> failed".
// AN EXPLICIT USER RETRY WITH NO LIVE SESSION WIPES THE CACHE FIRST.
function clearWcStorage() {

  try {

    for (let i = localStorage.length - 1; i >= 0; i--) {

      const key = localStorage.key(i);
      if (key && key.startsWith("wc@2"))
        localStorage.removeItem(key);
    }
    log.info("wallet-evm", "wc storage cleared for retry");
  } catch { /* STORAGE BLOCKED */ }
}

// WAIT UNTIL A FRESH URI EXISTS — FOR FLOWS WITHOUT A PRE-PAIRED MODAL (SIGN-IN)
export function waitWcReady(timeoutMs: number): Promise<string> {

  const now = wcUri();
  if (now)
    return Promise.resolve(now);

  return new Promise((resolve, reject) => {

    const timer = setTimeout(() => {

      unsub();
      reject(new Error("WalletConnect couldn't start — try again"));
    }, timeoutMs);
    const unsub = subscribeWc(() => {

      const u = wcUri();
      if (u) {

        clearTimeout(timer);
        unsub();
        resolve(u);
      } else if (phase === "failed") {

        clearTimeout(timer);
        unsub();
        reject(new Error("WalletConnect couldn't start — try again"));
      }
    });
  });
}
